'use client';

import React, { forwardRef, useState } from 'react';
import { UseFormRegister, RegisterOptions, Control, FieldPath, Controller } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormFieldError } from '@/components/ui/form-field-error';
// import { CheckCircle } from 'lucide-react';
// import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { FormFieldContext, useFormFieldContext, FormFieldContextValue } from './FormFieldContext';
import { Eye, EyeOff } from 'lucide-react';

// Main FormField compound component props
interface FormFieldProps {
  name: string;
  register?: UseFormRegister<any>;
  control?: Control<any>;
  error?: any;
  required?: boolean;
  disabled?: boolean;
  validation?: RegisterOptions<any>;
  className?: string;
  children: React.ReactNode;
  isDirty?: boolean;
  isValid?: boolean;
}

function FormFieldRoot({
  name,
  register,
  control,
  error,
  required = false,
  disabled = false,
  validation = {},
  className,
  children,
  isDirty,
  isValid,
}: FormFieldProps) {
  const fieldId = `form-field-${name}`;
  const errorId = `${fieldId}-error`;

  const contextValue: FormFieldContextValue = {
    name,
    fieldId,
    errorId,
    error,
    register,
    control,
    disabled,
    validation,
    required,
    isDirty,
    isValid,
  };

  return (
    <FormFieldContext.Provider value={contextValue}>
      <div className={cn('space-y-2', className)}>{children}</div>
    </FormFieldContext.Provider>
  );
}

// FormField.Label component
interface FormFieldLabelProps {
  children: React.ReactNode;
  className?: string;
}

const FormFieldLabel = ({ children, className }: FormFieldLabelProps) => {
  const { fieldId, error, required, isDirty, isValid } = useFormFieldContext();

  const hasError = Boolean(error);
  // Success logic: field is dirty, valid, and has no errors
  const showSuccess = isDirty && isValid && !hasError;

  return (
    <Label
      htmlFor={fieldId}
      className={cn(
        'text-sm font-medium flex items-center gap-1',
        hasError && 'text-red-600',
        showSuccess && '',
        className,
      )}>
      {children}
      {required && (
        <span className="text-red-600/60 ml-1" aria-label="required">
          *
        </span>
      )}
      {/* {showSuccess && (
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300 }}>
          <CheckCircle className="h-4 w-4 text-green-500" />
        </motion.div>
      )} */}
    </Label>
  );
};

// FormField.Input component with automatic validation rules
interface FormFieldInputProps extends Omit<React.ComponentProps<typeof Input>, 'name'> {
  type?: 'text' | 'email' | 'tel' | 'password' | 'number' | 'url' | 'time' | 'date' | 'datetime-local';
}

const FormFieldInput = forwardRef<HTMLInputElement, FormFieldInputProps>(
  ({ type = 'text', className, ...props }, ref) => {
    const {
      name,
      register,
      control,
      fieldId,
      errorId,
      error,
      disabled: contextDisabled,
      validation,
      required,
      isDirty,
      isValid,
    } = useFormFieldContext();

    const disabled = props.disabled ?? contextDisabled;
    const fieldName = name as FieldPath<any>;
    const hasError = Boolean(error);

    // Success logic: field is dirty, valid, and has no errors
    const showSuccess = isDirty && isValid && !hasError;

    // Track password visibility
    const [showPassword, setShowPassword] = useState(false);
    const inputType = type === 'password' ? (showPassword ? 'text' : 'password') : type;

    // Validation rules
    const validationRules: RegisterOptions<any> = {
      ...validation,
      ...(required && { required: 'This field is required' }),
    };

    const renderInput = (fieldProps: any = {}) => (
      <div className="relative w-full">
        <Input
          {...fieldProps}
          {...props}
          ref={ref}
          id={fieldId}
          type={inputType}
          disabled={disabled}
          error={hasError}
          success={showSuccess}
          className={className}
          aria-describedby={hasError ? errorId : undefined}
          aria-invalid={hasError}
        />
        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
            tabIndex={-1}>
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
    );

    if (control) {
      return (
        <Controller
          name={fieldName}
          control={control}
          rules={validationRules}
          render={({ field }) => renderInput(field)}
        />
      );
    }

    const registrationProps = register ? register(fieldName, validationRules) : {};

    return renderInput(registrationProps);
  },
);

FormFieldInput.displayName = 'FormFieldInput';

// FormField.Error component
interface FormFieldErrorProps {
  className?: string;
}

const FormFieldErrorComponent = ({ className }: FormFieldErrorProps) => {
  const { error } = useFormFieldContext();

  if (!error) return null;

  return <FormFieldError error={error.message} className={className} />;
};

// FormField.Description component
interface FormFieldDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

const FormFieldDescription = ({ children, className }: FormFieldDescriptionProps) => {
  return <p className={cn('text-xs text-gray-500 -mt-1', className)}>{children}</p>;
};

// Compound component with sub-components
const FormField = Object.assign(FormFieldRoot, {
  Label: FormFieldLabel,
  Input: FormFieldInput,
  Error: FormFieldErrorComponent,
  Description: FormFieldDescription,
});

export { FormField };
export type {
  FormFieldProps,
  FormFieldInputProps,
  FormFieldLabelProps,
  FormFieldErrorProps,
  FormFieldDescriptionProps,
};
